import { z } from 'zod';

/** Trust Services Criteria families. CC (Security) is the mandatory baseline. */
export const TrustFamily = z.enum(['CC', 'A', 'C', 'P', 'PI']);
export type TrustFamily = z.infer<typeof TrustFamily>;

/** The seven stakeholder roles a control can be owned by. */
export const StakeholderRole = z.enum([
  'security',
  'engineering',
  'it',
  'hr',
  'legal',
  'risk',
  'management',
]);
export type StakeholderRole = z.infer<typeof StakeholderRole>;

/** Current-state rating used in the Phase 2 gap analysis. */
export const ControlStatus = z.enum(['met', 'partial', 'gap', 'na']);
export type ControlStatus = z.infer<typeof ControlStatus>;

/**
 * A Phase 2 questionnaire answer. The four control statuses plus `unsure`,
 * the "I don't understand the question" option that flags a control for help
 * without counting it for or against readiness.
 */
export const GapAnswer = z.enum(['met', 'partial', 'gap', 'na', 'unsure']);
export type GapAnswer = z.infer<typeof GapAnswer>;

/** Evidence collection cadence across the Type 2 observation window. */
export const Cadence = z.enum([
  'Monthly',
  'Quarterly',
  'Annual',
  'Per-event',
  'One-time',
]);
export type Cadence = z.infer<typeof Cadence>;

/** A single control from the QCA / SOC 2 register. */
export const Control = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string(),
  section: z.string(),
  trustIds: z.array(z.string()),
  trustFamilies: z.array(TrustFamily),
  stakeholder: StakeholderRole,
  stakeholderLabel: z.string(),
  /** Plain-language question shown in the Phase 2 questionnaire. */
  question: z.string().optional(),
  /** One-line "what good looks like" helper under the question. */
  helper: z.string().optional(),
  /** Dummy sample answer shown in the question's hover tooltip. */
  example: z.string().optional(),
});
export type Control = z.infer<typeof Control>;

export const ControlRegister = z.object({
  framework: z.string(),
  companyToken: z.string(),
  roleTaxonomy: z.record(StakeholderRole, z.string()),
  controls: z.array(Control),
});
export type ControlRegister = z.infer<typeof ControlRegister>;

/** A control enriched with the demo gap-analysis state shown in the kit. */
export interface AssessedControl extends Control {
  status: ControlStatus;
  cadence: Cadence;
}

/* ----- System description (AICPA DC 1-9) ----- */

export const SysDescFieldType = z.enum(['text', 'upload', 'table']);

export const SysDescField = z.object({
  id: z.string(),
  label: z.string(),
  type: SysDescFieldType,
  guidance: z.string().optional(),
  policy: z.string().optional(),
  attachment: z.string().optional(),
  conditionalTsc: z.string().optional(),
  columns: z.array(z.string()).optional(),
  sample: z.array(z.array(z.string())).optional(),
});
export type SysDescField = z.infer<typeof SysDescField>;

export const SysDescGroup = z.object({
  id: z.string(),
  title: z.string(),
  fields: z.array(SysDescField),
});
export type SysDescGroup = z.infer<typeof SysDescGroup>;

export const SystemDescriptionTemplate = z.object({
  title: z.string(),
  standard: z.string(),
  companyToken: z.string(),
  note: z.string().optional(),
  groups: z.array(SysDescGroup),
});
export type SystemDescriptionTemplate = z.infer<typeof SystemDescriptionTemplate>;

/* ----- Policy templates ----- */

export const PolicyTokenType = z.enum(['auto', 'input', 'choice']);
export type PolicyTokenType = z.infer<typeof PolicyTokenType>;

export const PolicyToken = z.object({
  id: z.string(),
  type: PolicyTokenType,
  label: z.string(),
  guidance: z.string().optional(),
  value: z.union([z.string(), z.undefined()]).optional(),
  options: z.array(z.string()).optional(),
});
export type PolicyToken = z.infer<typeof PolicyToken>;

export const PolicyBodySegment = z.union([
  z.object({ h: z.string() }),
  z.object({ p: z.string() }),
]);
export type PolicyBodySegment = z.infer<typeof PolicyBodySegment>;

export const PolicyStatus = z.enum(['adopted', 'customizing', 'not-started']);
export type PolicyStatus = z.infer<typeof PolicyStatus>;

export const PolicyDoc = z.object({
  id: z.string(),
  name: z.string(),
  controls: z.array(z.string()),
  status: PolicyStatus,
  summary: z.string(),
  tokens: z.array(PolicyToken),
  body: z.array(PolicyBodySegment),
});
export type PolicyDoc = z.infer<typeof PolicyDoc>;

export const PolicyTemplates = z.object({
  title: z.string(),
  note: z.string().optional(),
  tokenTypes: z.record(z.string(), z.string()),
  policies: z.array(PolicyDoc),
});
export type PolicyTemplates = z.infer<typeof PolicyTemplates>;

/* ----- Engagement profile + phases ----- */

export const ReportType = z.enum(['type1', 'type2']);
export type ReportType = z.infer<typeof ReportType>;

export const EngagementProfile = z.object({
  companyName: z.string(),
  reportType: ReportType,
  trustFamilies: z.array(TrustFamily),
  windowStart: z.string().optional(),
  windowEnd: z.string().optional(),
});
export type EngagementProfile = z.infer<typeof EngagementProfile>;

export const PhaseId = z.enum(['p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6']);
export type PhaseId = z.infer<typeof PhaseId>;

export const PhaseStatus = z.enum(['done', 'active', 'locked']);
export type PhaseStatus = z.infer<typeof PhaseStatus>;

/* ----- Phase 1: onboarding intake ----- */

export const OnboardingFieldType = z.enum([
  'text',
  'textarea',
  'choice',
  'multi',
  'list',
  'raci',
]);
export type OnboardingFieldType = z.infer<typeof OnboardingFieldType>;

export const OnboardingField = z.object({
  id: z.string(),
  label: z.string(),
  type: OnboardingFieldType,
  guidance: z.string().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(),
  required: z.boolean().optional(),
  /** Note shown when this answer also feeds the system description. */
  feeds: z.string().optional(),
});
export type OnboardingField = z.infer<typeof OnboardingField>;

export const OnboardingGroup = z.object({
  id: z.string(),
  title: z.string(),
  intro: z.string().optional(),
  fields: z.array(OnboardingField),
});
export type OnboardingGroup = z.infer<typeof OnboardingGroup>;

export const OnboardingTemplate = z.object({
  title: z.string(),
  note: z.string().optional(),
  groups: z.array(OnboardingGroup),
});
export type OnboardingTemplate = z.infer<typeof OnboardingTemplate>;

/* ----- Phase 1: pre-work checklist ----- */

export const PreworkItem = z.object({
  id: z.string(),
  label: z.string(),
  detail: z.string(),
  role: StakeholderRole,
});
export type PreworkItem = z.infer<typeof PreworkItem>;

/* ----- Phase 1: kickoff ----- */

export const KickoffPhase = z.object({
  id: PhaseId,
  n: z.string(),
  label: z.string(),
  lead: StakeholderRole,
  /** Relative duration used to size the animated timeline segment. */
  weeks: z.number(),
  blurb: z.string(),
});
export type KickoffPhase = z.infer<typeof KickoffPhase>;

/** A single slide in the kickoff deck. `kind` drives special rendering. */
export const KickoffSlideKind = z.enum([
  'title',
  'content',
  'timeline',
  'roles',
  'closing',
]);
export type KickoffSlideKind = z.infer<typeof KickoffSlideKind>;

export const KickoffSlide = z.object({
  id: z.string(),
  kind: KickoffSlideKind.optional(),
  kicker: z.string().optional(),
  title: z.string(),
  /** Optional lead-in paragraph shown above any bullets. */
  lead: z.string().optional(),
  bullets: z.array(z.string()).optional(),
  note: z.string().optional(),
});
export type KickoffSlide = z.infer<typeof KickoffSlide>;

export const Kickoff = z.object({
  title: z.string(),
  intro: z.string(),
  phases: z.array(KickoffPhase),
  agenda: z.array(z.string()),
  deck: z.array(KickoffSlide),
});
export type Kickoff = z.infer<typeof Kickoff>;
